import type { AppContext } from "../../app/app";
import type { Person, Tag } from "../../data/model";
import { resolveProfile } from "../../data/resolve";
import { purposesPresent } from "../../data/packs";
import { domainsPresent } from "../../data/domains";
import { el } from "../dom";
import { renderImportPanel } from "../import-panel";
import { renderPurposeFilter } from "../dimension-tools";

// Prefilled Assemble prompt when resurfacing a known person.
export function assemblePromptFor(name: string): string {
  return `What is my read on ${name}, and where should I rely on or be wary of them?`;
}

// People list with add, import, search, and decision-moment resurfacing: typing
// or searching a name you have already rated surfaces that person's tags and
// offers to run Assemble on them.
export async function renderPeople(ctx: AppContext): Promise<HTMLElement> {
  const [people, dimensions, tags, topics, personTopics] = await Promise.all([
    ctx.repo.listPeople(),
    ctx.repo.listDimensions(),
    ctx.repo.listTags({ viewer_id: ctx.viewer }),
    ctx.repo.listTopics(),
    ctx.repo.listPersonTopics(),
  ]);
  const dimName = new Map(dimensions.map((d) => [d.id, d.name]));
  const dimPurposes = new Map(dimensions.map((d) => [d.id, d.purposes]));
  const byPerson = new Map<string, Tag[]>();
  for (const t of tags) {
    const list = byPerson.get(t.person_id) ?? [];
    list.push(t);
    byPerson.set(t.person_id, list);
  }

  // Active purpose scope for the list (null = all). A person matches a purpose
  // when they carry a tag on one of that purpose's dimensions.
  let activePurpose: string | null = null;
  const matchesPurpose = (p: Person) =>
    activePurpose === null ||
    (byPerson.get(p.id) ?? []).some((t) =>
      (dimPurposes.get(t.dimension_id) ?? []).includes(activePurpose!),
    );

  // Active domain filter (null = all). A separate axis: a person matches when
  // they are flagged with that domain.
  const domainsByPerson = new Map<string, Set<string>>();
  for (const pt of personTopics) {
    const set = domainsByPerson.get(pt.person_id) ?? new Set<string>();
    set.add(pt.topic_id);
    domainsByPerson.set(pt.person_id, set);
  }
  let activeDomain: string | null = null;
  const matchesDomain = (p: Person) =>
    activeDomain === null || (domainsByPerson.get(p.id)?.has(activeDomain) ?? false);

  const root = el("section", {});
  const resurfaceHost = el("div", {});
  const listHost = el("div", { class: "people-list" });

  // Cap the rendered rows so a large imported book (thousands of contacts) stays
  // fast; the count and a nudge tell the user to search to narrow.
  const LIST_CAP = 60;
  const renderRows = (query: string) => {
    const q = query.trim().toLowerCase();
    listHost.replaceChildren();
    const shown = people.filter(
      (p) =>
        matchesPurpose(p) &&
        matchesDomain(p) &&
        (!q ||
          p.display_name.toLowerCase().includes(q) ||
          p.relationship_context.toLowerCase().includes(q)),
    );
    for (const p of shown.slice(0, LIST_CAP))
      listHost.append(personRow(ctx, p, byPerson.get(p.id)?.length ?? 0));
    if (shown.length === 0) {
      listHost.append(el("div", { class: "people-more", text: "No match." }));
    } else if (shown.length > LIST_CAP) {
      listHost.append(
        el("div", {
          class: "people-more",
          text: `Showing ${LIST_CAP} of ${shown.length}. Search to narrow.`,
        }),
      );
    }
  };

  const maybeResurface = (query: string) => {
    resurfaceHost.replaceChildren();
    const q = query.trim().toLowerCase();
    if (q.length < 2) return;
    const hits = people.filter(
      (p) => (byPerson.get(p.id)?.length ?? 0) > 0 && p.display_name.toLowerCase().includes(q),
    );
    if (hits.length === 1) {
      resurfaceHost.append(
        resurfaceCard(ctx, hits[0]!, byPerson.get(hits[0]!.id) ?? [], dimName),
      );
    }
  };

  const search = el("input", {
    class: "input people-search",
    type: "text",
    placeholder: "Search people by name or company",
  });
  search.addEventListener("input", () => {
    renderRows(search.value);
    maybeResurface(search.value);
  });

  root.append(
    el("span", { class: "label", text: "People" }),
    el("h1", { text: "Whose judgement do you hold?" }),
    el("p", {
      class: "lede",
      text:
        "Secondary to the decision view: the ledger of people you have a read " +
        "on. Add one, import contacts, or search someone you have rated.",
    }),
    addForm(ctx, people),
    // The cold-start import card only leads when there is little data; past a
    // small threshold it becomes a quiet inline action.
    renderImportPanel(ctx, people.length > 10),
  );

  if (people.length === 0) {
    root.append(
      el(
        "div",
        { class: "empty" },
        el("span", { class: "label", text: "No people yet" }),
        "Add someone above, or import contacts, to begin rating.",
      ),
    );
    return root;
  }

  renderRows("");
  const purposeFilter = renderPurposeFilter(purposesPresent(dimensions), (key) => {
    activePurpose = key;
    renderRows(search.value);
  });
  const domainFilter = renderPurposeFilter(domainsPresent(topics, personTopics), (key) => {
    activeDomain = key;
    renderRows(search.value);
  });
  root.append(
    el(
      "div",
      { class: "people-bar" },
      el("span", { class: "label", text: `${people.length} people` }),
      search,
    ),
  );
  if (purposeFilter) root.append(purposeFilter);
  if (domainFilter) {
    root.append(el("span", { class: "label", text: "Domain" }), domainFilter);
  }
  root.append(resurfaceHost, listHost);
  return root;
}

function personRow(ctx: AppContext, p: Person, count: number): HTMLElement {
  return el(
    "button",
    {
      class: "person-row",
      on: { click: () => ctx.navigate({ name: "person", id: p.id }) },
    },
    el(
      "span",
      { class: "person-row__main" },
      el("span", { class: "person-row__name", text: p.display_name }),
      p.relationship_context
        ? el("span", { class: "person-row__meta", text: p.relationship_context })
        : null,
    ),
    el("span", {
      class: "badge num",
      text: count === 0 ? "untagged" : `${count} rated`,
    }),
  );
}

// Decision-moment resurfacing: a known person's existing tags, with a direct
// route into Assemble on them. Purely contextual; nothing is sent anywhere.
function resurfaceCard(
  ctx: AppContext,
  person: Person,
  personTags: Tag[],
  dimName: Map<string, string>,
): HTMLElement {
  const tagsList = el("div", { class: "resurface__tags" });
  for (const t of [...personTags].sort((a, b) => b.rating - a.rating)) {
    tagsList.append(
      el(
        "div",
        { class: "resurface__tag" },
        el("span", { class: "resurface__dim", text: dimName.get(t.dimension_id) ?? "?" }),
        el("span", { class: "resurface__rating num", text: `${t.rating}/5` }),
        t.rationale
          ? el("span", { class: "resurface__why", text: t.rationale })
          : null,
      ),
    );
  }
  return el(
    "div",
    { class: "resurface" },
    el(
      "div",
      { class: "resurface__head" },
      el("span", { class: "label", text: "Already rated" }),
      el("span", { class: "resurface__name", text: person.display_name }),
    ),
    tagsList,
    el(
      "div",
      { class: "panel-actions" },
      el("button", {
        class: "btn btn--primary",
        text: "Assemble on them",
        on: {
          click: () =>
            ctx.navigate({ name: "assemble", prompt: assemblePromptFor(person.display_name) }),
        },
      }),
      el("button", {
        class: "btn btn--ghost",
        text: "Open",
        on: { click: () => ctx.navigate({ name: "person", id: person.id }) },
      }),
    ),
  );
}

function addForm(ctx: AppContext, existing: Person[]): HTMLElement {
  const name = el("input", {
    class: "input",
    type: "text",
    placeholder: "Name or profile URL",
  });
  const context = el("input", {
    class: "input",
    type: "text",
    placeholder: "Relationship context (optional)",
  });
  const error = el("span", { class: "error" });

  const submit = async () => {
    const resolved = resolveProfile(name.value);
    if (!resolved.display_name) {
      error.textContent = "Enter a name or profile URL.";
      return;
    }
    error.textContent = "";
    // If this person already exists, go to them (resurfacing) rather than
    // creating a duplicate.
    const match = existing.find(
      (p) =>
        (resolved.target_key && p.target_key === resolved.target_key) ||
        p.display_name.toLowerCase() === resolved.display_name.toLowerCase(),
    );
    if (match) {
      ctx.navigate({ name: "person", id: match.id });
      return;
    }
    await ctx.repo.addPerson({
      display_name: resolved.display_name,
      target_key: resolved.target_key,
      source: resolved.source,
      relationship_context: context.value.trim(),
    });
    name.value = "";
    context.value = "";
    ctx.refresh();
  };

  return el(
    "div",
    { class: "add-form" },
    el(
      "div",
      { class: "add-form__row" },
      name,
      context,
      el("button", {
        class: "btn btn--primary",
        text: "Add person",
        on: { click: () => void submit() },
      }),
    ),
    error,
  );
}
